import _mergewith from 'lodash/mergeWith'
import _pick from 'lodash/pick'
import _uniq from 'lodash/uniq'
import _omit from 'lodash/omit'
import _isequal from 'lodash/isEqual'
import _set from 'lodash/set'
import _get from 'lodash/get'
import type { WriteTransaction } from 'replicache'
import {
  blockSchema,
  isBlockWithChildren,
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  isRichTextBlock,
  isRootBlock,
  isDocumentBlock,
  isCommentBlock,
  isNonCrucialBlock,
  makeInstanceChildBlock,
  isValidBlockPreviewType,
  shouldForcefullyPropagateHiddenState,
  resolveInheritedHiddenState,
  normalizeStringPath,
  blockRelationshipBaseOps,
  isReferenceBlock,
  getInstanceChildId,
  migrateCurrentBlockToNewType,
  isStructuralBr,
  getBRId,
  isVirtualInstanceChildTarget,
} from '@basehub/data/replicache/index'
import type {
  Block,
  BlockRelationship,
  CommonBlocksCompoundIndexes,
  InstanceBlock,
  ListBlock,
  RichTextBlock,
  RootBlock,
} from '@basehub/data/replicache/index'
import type { PartialDeep } from 'type-fest'
import { invariant } from '@epic-web/invariant'
import { getIdempotentId } from '@basehub/utils/id/index'
import { validateBlockNestingPromise } from '../helpers/component-helpers'
import {
  checkComponentDeletion,
  detachInstancesOfComponent,
  syncAllRichTextsUsingComponent,
} from './component'
import { getNewBlockDataByType } from '@basehub/data/replicache/helpers/get-new-block-data-by-type'
import { match } from 'ts-pattern'
import { detachBlocksFromTarget } from './reference'
import { updateRichTextStateBasedOnNewChildren } from './rich-text'
import { propagateHiddenStateToChildren } from './hidden'
import { isInstanceInRichText } from '@basehub/tiptap-api-utils/rich-text-helpers'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { hasAccessOrThrow } from '@basehub/data/replicache/mutators/team-space/team/auth'
import {
  BaseHubBlock,
  BaseHubInlineBlock,
  getIdFromBaseHubNode,
  getInstancesInRichText,
  jsonToYDocBase64State,
  mapJSONNode,
  richTextExtensionsForTransformations,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { editorCanEditBlocks } from '@basehub/data/replicache/schema/block/permissions'
import {
  doCopyOnWrite,
  getCoRBlockKey,
  idToSourceBlockId,
  mergeBlockWithCopyOnWriteProps,
} from './copy-on-write'
import { TreeManager } from '@basehub/utils/tree/index'
import { BlockBatchMutationParams } from './batch'
import { trimUndefined } from 'compress-json'
import {
  BaseHubTransaction,
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import {
  Position,
  generateKeyBetween,
  getOrderFromPosition,
} from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'
import pMap from 'p-map'
import { hashJson } from '@basehub/utils/hash'
import { revalidateBlockMilestone } from '../helpers/revalidate-milestone'
import { PerfReport } from '@basehub/utils/perf/index'
import { run } from '@basehub/utils'

export async function getBlocks(
  tx: WriteTransaction,
  ids: string[],
  expectNotFoundForBlockIds?: string[]
): Promise<Array<Block>> {
  ensureBaseHubTransaction(tx)
  return (
    await Promise.all(
      ids.map(async (id) => {
        const b = await blockBaseOps.get(tx, id)
        if (!b) {
          if (expectNotFoundForBlockIds?.includes(id)) return null
          throw new Error(`Block with id ${id} not found`)
        }
        return b
      })
    )
  ).filter(Boolean)
}

const DEBUG = false

/* -------------------------------------------------------------------------------------------------
 * CREATE
 * -----------------------------------------------------------------------------------------------*/

export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>

export type CreateBlockParams = {
  path: string
  isoNow: string
  idSeed: string
  data: AtLeast<Block, 'id' | 'type'>
  position?: Position
  skipRehashing?: boolean
  skipBrCreation?: boolean
  skipInstanceValidation?: boolean
  skipPropagateHiddenState?: boolean
  skipUpdatingParent?: boolean
  parentInstanceChildTarget: string | null | undefined
  prefetchedParentBlock?: Block
}

export const createBlock = async (
  tx: WriteTransaction,
  {
    path,
    data,
    isoNow,
    idSeed,
    skipBrCreation,
    skipUpdatingParent,
    skipInstanceValidation,
    skipPropagateHiddenState,
    position,
    parentInstanceChildTarget,
    prefetchedParentBlock,
  }: WithServerSecret<CreateBlockParams>
) => {
  ensureBaseHubTransaction(tx)

  let newBlockPath = path

  const splittedPathMut = normalizeStringPath({
    path,
    edgeId: data.id,
    includeEdge: false,
  })
  const splittedPath = normalizeStringPath({
    path,
    edgeId: data.id,
    includeEdge: false,
  })

  const rootBlockId = splittedPathMut[0]
  invariant(rootBlockId, 'rootBlockId not defined')

  const parentBlockId = splittedPathMut.pop()
  if (!parentBlockId) throw new Error('Parent block id not found')

  let rootBlock: RootBlock | null
  let parentBlock: Block | null
  if (parentBlockId === rootBlockId) {
    rootBlock = (await blockBaseOps.get(tx, rootBlockId)) as RootBlock | null
    parentBlock = rootBlock
    newBlockPath = splittedPath.join('/') + '/' + data.id
  } else {
    if (prefetchedParentBlock) {
      rootBlock = (await blockBaseOps.get(tx, rootBlockId)) as RootBlock | null
      parentBlock = prefetchedParentBlock
      newBlockPath = splittedPath.join('/') + '/' + data.id
    } else {
      const cor = await doCopyOnWrite(tx, {
        partialBlock: {
          id: parentBlockId,
          instanceChildTarget: parentInstanceChildTarget,
        },
        isoNow,
        path: splittedPath.join('/'),
      })
      parentBlock = cor.block
      rootBlock = cor.rootBlock
      newBlockPath = cor.path + '/' + data.id
    }
  }

  invariant(rootBlock, 'Root block not found')
  invariant(parentBlock, 'Parent block not found')
  if (!isBlockWithChildren(parentBlock)) {
    throw new Error(`Parent block type ${parentBlock.type} not valid`)
  }

  if (
    editorCanEditBlocks({
      parentBlock: parentBlock,
      blocks: [data],
    })
  ) {
    // if it's a list child, we ensure the user is at least editor
    hasAccessOrThrow({
      serverOnly: tx.serverOnly,
      clientOnly: tx.clientOnly,
      serverSecret: tx.serverSecret,
      allowedRoles: ['editor', 'owner', 'member'],
      guestAccess: 'branch-author',
    })
  } else {
    // ensure user is owner or member
    hasAccessOrThrow({
      serverOnly: tx.serverOnly,
      clientOnly: tx.clientOnly,
      serverSecret: tx.serverSecret,
      allowedRoles: ['owner', 'member'],
      guestAccess: 'branch-author',
    })
  }

  let componentForListBlockId = ''
  let typeOptions = data.typeOptions
  if (data.type === 'list' && !data.typeOptions?.targetBlockId) {
    componentForListBlockId = idSeed = getIdempotentId({ idSeed })
    typeOptions = {
      targetBlockId: componentForListBlockId,
      ...data.typeOptions,
    }
  }

  const blockSpecificStuff = getNewBlockDataByType({
    ...data,
    typeOptions,
  } as typeof data)

  let sourceBlockId = data.sourceBlockId ?? null
  if (isInstanceBlock(parentBlock)) {
    invariant(data.instanceChildTarget, 'targetBlock not found')
    sourceBlockId = getInstanceChildId({
      parentSourceBlockId: idToSourceBlockId(parentBlock),
      targetSourceBlockId: data.instanceChildTarget,
    })
  }

  const playgroundId =
    tx.clientOnly?.playgroundId ?? tx.serverOnly?.playgroundId
  const newBlock: Block = {
    ...blockSpecificStuff,
    id: data.id,
    title: data.title ?? '',
    hidden: data.hidden ?? false,
    createdAt: isoNow,
    isCommitted: false,
    sourceBlockId,
    ...(playgroundId ? { playgroundId } : {}),
  }
  trimUndefined(newBlock)

  if (!skipPropagateHiddenState) {
    const result = await resolveInheritedHiddenState(newBlock, path, (id) =>
      blockBaseOps.get(tx, id)
    )
    if (result.shouldInherit) {
      newBlock.hidden = result.valueToInherit
    }
  }

  if (
    (isInstanceBlock(newBlock) ||
      isDocumentBlock(newBlock) ||
      isComponentBlock(newBlock)) &&
    !skipInstanceValidation
  ) {
    const result = await validateBlockNestingPromise({
      block: newBlock,
      path,
      shouldThrowIfInvalid: false,
      fetchChildren: async (sourceBlockId) => {
        const brs = await tx.getBlockChildren({ sourceBlockId, rootBlockId })
        return await getBlocks(
          tx,
          brs.map((br) => br.blockId)
        )
      },
      fetchBlock: async (idOrSourceBlockId) => {
        const br = await tx.getBlockRelationshipByBlockId({
          blockId: idOrSourceBlockId,
          rootBlockId,
        })
        // virtual block
        if (!br) return undefined
        return await blockBaseOps.get(tx, br.blockId)
      },
    })

    if (!result.isValid) throw new MutatorUserError(result.reason)
  }

  if (
    isInstanceBlock(newBlock) ||
    (isListBlock(newBlock) && !componentForListBlockId)
  ) {
    if (tx.serverOnly) {
      // ensure targetBlockId exists
      const treeManager = await tx.getTreeManager(rootBlockId)
      const targetBlock = treeManager.getNode(
        newBlock.typeOptions.targetBlockId
      )
      if (!targetBlock) {
        throw new MutatorUserError(
          `Target block ${newBlock.typeOptions.targetBlockId} not found`
        )
      }
    }
  }

  let newBlockRelationship: BlockRelationship | null = null
  if (!skipBrCreation) {
    const order = await getOrderFromPosition(tx, {
      position: position ?? { type: 'last' },
      parentSourceBlockId: idToSourceBlockId(parentBlock),
      rootBlockId,
    })
    newBlockRelationship = {
      branchId: rootBlock.branchId,
      validFrom: rootBlock.createdAt,
      id: getBRId(newBlock.id, rootBlockId),
      sourceBlockId: idToSourceBlockId(newBlock),
      blockId: newBlock.id,
      parentId: idToSourceBlockId(parentBlock),
      order,
      isCommitted: false,
      structural: isStructuralBr({ parent: parentBlock, block: newBlock }),
      ...(rootBlock.isInlineSuggestion ? { isInlineSuggestion: true } : {}),
    }
    await blockRelationshipBaseOps.set(tx, newBlockRelationship)
  }

  let parentToUpdate
  if (isReferenceBlock(parentBlock)) {
    if (isComponentBlock(newBlock)) {
      // if its a component, we'll also link it to the union
      parentToUpdate = { ...parentBlock }
      parentToUpdate.constraints.acceptedTypes = _uniq([
        ...(parentBlock.constraints.acceptedTypes ?? []),
        idToSourceBlockId(newBlock),
      ])
    } else if (
      // is not instance
      !isInstanceBlock(newBlock)
    ) {
      throw new Error(
        `Invalid union child with id ${idToSourceBlockId(
          newBlock
        )} and type ${newBlock.type}`
      )
    }
  } else if (isRichTextBlock(parentBlock)) {
    if (isComponentBlock(newBlock)) {
      // if its a component, we'll also link it to the union
      parentToUpdate = { ...parentBlock }
      parentToUpdate.constraints.allowedComponents = _uniq([
        ...(parentBlock.constraints.allowedComponents ?? []),
        idToSourceBlockId(newBlock),
      ])
    } else if (
      // is not instance
      !isInstanceBlock(newBlock)
    ) {
      throw new Error(
        `Invalid rich text child with id ${idToSourceBlockId(
          newBlock
        )} and type ${newBlock.type}`
      )
    }
  } else if (isListBlock(parentBlock)) {
    if (!isComponentBlock(newBlock) && !isInstanceBlock(newBlock)) {
      throw new Error(
        `Invalid list child with id ${idToSourceBlockId(newBlock)} and type ${
          newBlock.type
        }`
      )
    }
  }

  // first update the parent, so if it does copy-on-write, we'll get the correct id in the next step
  if (!skipUpdatingParent && parentToUpdate) {
    const parentUpdateResult = await updateBlock(tx, {
      id: parentToUpdate.id,
      instanceChildTarget: parentToUpdate.instanceChildTarget,
      data: parentToUpdate,
      path: normalizeStringPath({
        path: newBlockPath,
        edgeId: data.id,
        includeEdge: false,
        format: 'string',
      }),
      strategy: 'replace',
      isoNow,
    })
    parentBlock = parentUpdateResult.updatedBlock
  }

  // then create the block and its milestone
  await blockBaseOps.put(tx, newBlock, parentBlock.id)
  tx.revalidateBlockMilestonesMap.set(newBlock.id, {
    path,
    sourceBlockId: newBlock.sourceBlockId ?? newBlock.id,
    rootId: rootBlockId,
    isoNow,
    blockId: newBlock.id,
    isPropagating: null,
  })

  // further work for list type blocks.
  if (newBlock.type === 'list' && componentForListBlockId) {
    // we need to create a component block to use in the list.
    const pathForComponentBlock = normalizeStringPath({
      path,
      edgeId: newBlock.id,
      includeEdge: true,
      format: 'string',
    })
    const { newBlock: blockCreated } = await createBlock(tx, {
      data: {
        id: componentForListBlockId,
        title: '',
        type: 'component',
        hidden: true,
      },
      position: {
        type: 'specific',
        order: generateKeyBetween(null, null, rootBlockId),
      },
      parentInstanceChildTarget: newBlock.instanceChildTarget,
      path: pathForComponentBlock,
      isoNow,
      idSeed: (idSeed = getIdempotentId({ idSeed })),
    })
    invariant(
      blockCreated.type === 'component',
      `blockCreated ${blockCreated} should be of type "component". this is a bug in basehub`
    )
  }

  return {
    newBlock: structuredClone(newBlock),
    newBlockRelationship: structuredClone(newBlockRelationship),
    parentBlock,
    newBlockPath,
    undo: [
      {
        op: 'deleteBlock',
        args: {
          id: newBlock.id,
          idSeed: (idSeed = getIdempotentId({ idSeed })),
          isoNow,
          path,
        },
      },
    ] satisfies BlockBatchMutationParams['mutations'],
  }
}

/* -------------------------------------------------------------------------------------------------
 * UPDATE
 * -----------------------------------------------------------------------------------------------*/

export function mergeDeepForUpdate<T>(block: T, data: T | PartialDeep<T>): T {
  return _mergewith({ ...block }, data, (_, newValue) => {
    if (Array.isArray(newValue)) {
      // just replace
      return newValue
    }
  })
}

export type UpdateBlockParams = {
  path: string
  isoNow: string
  id: string
  instanceChildTarget: string | null | undefined
  ignoreHashComparison?: boolean
  forceComponentInstancesSync?: { idSeed: string }
  skipPropagateHiddenState?: boolean
  ignoreHiddenPropagationWarning?: boolean
  skipComponentHeirarchyCheck?: boolean
  skipYjsStateCleanup?: boolean
} & (
  | { strategy: 'merge'; data: AtLeast<Block, 'type'> }
  | { strategy: 'merge-deep'; data: PartialDeep<Block> }
  | { strategy: 'replace'; data: Block }
)

export const updateBlock = async (
  tx: WriteTransaction,
  {
    path: _path,
    id: _id,
    instanceChildTarget,
    data: _data,
    strategy,
    isoNow,
    ignoreHashComparison = true,
    forceComponentInstancesSync,
    skipPropagateHiddenState,
    ignoreHiddenPropagationWarning,
    skipComponentHeirarchyCheck,
  }: WithServerSecret<UpdateBlockParams>
) => {
  ensureBaseHubTransaction(tx)

  const perfReport = undefined as undefined | PerfReport // tx.serverOnly?.perfReport

  const data = structuredClone(_data)

  const {
    block: blockToUpdate,
    blockRelationship,
    parentBlock,
    rootBlock,
    path,
    didCopy,
  } = await doCopyOnWrite(tx, {
    partialBlock: { id: _id, instanceChildTarget },
    path: _path,
    isoNow,
  })

  perfReport?.checkpoint('updateBlock:DO_COPY_ON_WRITE')

  const normalizedPath = normalizeStringPath({ path, edgeId: blockToUpdate.id })

  if (blockToUpdate.isCommitted) {
    throw new Error('Cannot update committed block.')
  }

  const isUpdatingSchema = () => {
    let _isUpdatingSchema = false
    for (const key in data) {
      const typedKey = key as keyof typeof data
      // Skip keys that are not actually being updated
      if (_isequal(data[typedKey], blockToUpdate[typedKey])) {
        continue
      }

      // @ts-expect-error
      if (typedKey !== 'value' && typedKey !== 'variantOverrides') {
        if (
          typedKey === 'title' &&
          isNonCrucialBlock(blockToUpdate, parentBlock)
        ) {
          // if it's a title change that doesn't affect the API schema, we can allow it
          continue
        }
        _isUpdatingSchema = true
        break
      }
    }
    return _isUpdatingSchema
  }

  if (
    editorCanEditBlocks({
      parentBlock: parentBlock,
      blocks: [blockToUpdate],
    }) ||
    !isUpdatingSchema()
  ) {
    hasAccessOrThrow({
      serverOnly: tx.serverOnly,
      clientOnly: tx.clientOnly,
      serverSecret: tx.serverSecret,
      allowedRoles: ['editor', 'owner', 'member'],
      guestAccess: 'branch-author',
    })
  } else {
    hasAccessOrThrow({
      serverOnly: tx.serverOnly,
      clientOnly: tx.clientOnly,
      serverSecret: tx.serverSecret,
      allowedRoles: ['owner', 'member'],
      guestAccess: 'branch-author',
    })
  }

  // Compute dataForUndo based on the strategy
  let dataForUndo: PartialDeep<Block> = {}
  if (strategy === 'replace') {
    dataForUndo = blockToUpdate
  } else if (strategy === 'merge') {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = blockToUpdate[key as keyof Block]
        if (value !== undefined) {
          // eslint-disable-next-line no-extra-semi
          ;(dataForUndo as Record<string, unknown>)[key] = value
        }
      }
    }
  } else if (strategy === 'merge-deep') {
    const collectUndoData = <T extends Record<string, unknown>>(
      source: T,
      target: T,
      currentPath: string[] = []
    ) => {
      for (const key in target) {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
          if (
            typeof target[key] === 'object' &&
            target[key] !== null &&
            !Array.isArray(target[key])
          ) {
            if (source[key]) {
              collectUndoData(source[key] as T, target[key] as T, [
                ...currentPath,
                key,
              ])
            }
          } else {
            // If it's an array or primitive, we don't merge, so we collect the whole path
            const value = source[key]
            if (value !== undefined) {
              _set(dataForUndo, [...currentPath, key], value)
            }
          }
        }
      }
    }
    collectUndoData(blockToUpdate, data)
  }

  dataForUndo = structuredClone(dataForUndo)

  if (
    isRichTextBlock(blockToUpdate) &&
    (!data.type || data.type === 'rich-text')
  ) {
    // update rich text overrides hashes if they were updated
    if ('variantOverrides' in data && data.variantOverrides) {
      Object.entries(data.variantOverrides).forEach(([key, _value]) => {
        const value = _value as NonNullable<
          RichTextBlock['variantOverrides']
        >[string]
        const json = stateToJson(value.value.state)
        const overrideLivingChildren = getInstancesInRichText(
          value.value.state,
          json
        )
        if (data.variantOverrides?.[key]) {
          data.variantOverrides[key] = {
            ...value,
            hash: hashJson(json),
            overrideLivingChildren,
          } satisfies NonNullable<RichTextBlock['variantOverrides']>[string]
        }
      })
    }
  }

  let processedData: Block = structuredClone(
    strategy === 'merge-deep'
      ? mergeDeepForUpdate(blockToUpdate, data)
      : strategy === 'merge'
        ? blockSchema.parse({ ...blockToUpdate, ...data })
        : blockSchema.parse(data) // replace
  )

  if (didCopy) {
    // overwrite copy-on-write keys
    processedData = mergeBlockWithCopyOnWriteProps(processedData, blockToUpdate)
  }

  const undoParams = {
    id: blockToUpdate.id,
    instanceChildTarget,
    data: dataForUndo as Block,
    strategy,
    path,
    isoNow,
    ignoreHashComparison,
    ignoreHiddenPropagationWarning,
    forceComponentInstancesSync,
    skipPropagateHiddenState,
  } satisfies UpdateBlockParams
  trimUndefined(undoParams)

  const undo: BlockBatchMutationParams['mutations'] = [
    { op: 'updateBlock', args: undoParams },
  ]

  if (tx.serverOnly) {
    /**
     * Clean up children that are no longer in the rich-text.
     * Do it just in the server for better performance.
     */
    if (isRichTextBlock(processedData)) {
      const currentChildren = await tx.getBlockChildren({
        sourceBlockId: idToSourceBlockId(processedData),
        rootBlockId: rootBlock.id,
      })

      const livingChildren = new Set<string>()
      if (processedData.variantOverrides) {
        for (const [oKey, override] of Object.entries(
          processedData.variantOverrides
        )) {
          if (override.overrideLivingChildren) {
            for (const c of override.overrideLivingChildren) {
              livingChildren.add(c)
            }
          } else {
            const instancesInRichText = getInstancesInRichText(
              override.value.state
            )
            for (const c of instancesInRichText) {
              livingChildren.add(c)
            }
            processedData.variantOverrides[oKey] = {
              ...override,
              overrideLivingChildren: instancesInRichText,
            } satisfies NonNullable<RichTextBlock['variantOverrides']>[string]
          }
        }
      }

      const instancesInRichText = getInstancesInRichText(
        processedData.value.state
      )
      for (const c of instancesInRichText) {
        livingChildren.add(c)
      }

      for (const c of processedData.constraints.allowedComponents ?? []) {
        livingChildren.add(c)
      }

      for (const c of processedData.constraints.allowedInternalLinks ?? []) {
        livingChildren.add(c)
      }

      const missingChildren = new Set(livingChildren)
      await pMap(currentChildren, async (childBr) => {
        if (livingChildren.has(childBr.sourceBlockId)) {
          missingChildren.delete(childBr.sourceBlockId)
          return
        }

        if (childBr.structural) {
          // for now, we'll spare components here
          // cause there are too many edge cases if we try to delete them here
          return
        }

        const references = await tx.getReferencesByTarget({
          targetBlockId: childBr.sourceBlockId,
          rootBlockId: rootBlock.id,
        })
        if (references.length > 0) {
          livingChildren.add(childBr.sourceBlockId)
          return
        }

        // else it should be cleaned up
        await blockRelationshipBaseOps.update(tx, {
          id: childBr.id,
          deletedAt: isoNow,
        })
        tx.revalidateBlockMilestonesMap.set(childBr.blockId, {
          path: normalizedPath.join('/') + '/' + childBr.blockId,
          blockId: childBr.blockId,
          isoNow,
          sourceBlockId: childBr.sourceBlockId,
          rootId: rootBlock.id,
          isPropagating: null,
        })
      })

      await pMap(missingChildren, async (cId) => {
        // re-place them
        const br = await tx.getBlockRelationshipByBlockId({
          blockId: cId,
          rootBlockId: rootBlock.id,
          includeDiscarded: true,
        })
        if (br?.discardedAt) {
          if (processedData.constraints.allowedComponents?.includes(cId)) {
            // clean it up
            processedData.constraints.allowedComponents =
              processedData.constraints.allowedComponents.filter(
                (c) => c !== cId
              )
          }
          if (processedData.constraints.allowedInternalLinks?.includes(cId)) {
            // clean it up
            processedData.constraints.allowedInternalLinks =
              processedData.constraints.allowedInternalLinks.filter(
                (c) => c !== cId
              )
          }
          return
        }
        invariant(br, `BR for block ${cId} and root ${rootBlock.id} not found`)
        await blockRelationshipBaseOps.update(tx, {
          id: br.id,
          deletedAt: null,
        })
        tx.revalidateBlockMilestonesMap.set(br.blockId, {
          path: normalizedPath.join('/') + '/' + br.blockId,
          blockId: br.blockId,
          isoNow,
          sourceBlockId: br.sourceBlockId,
          rootId: rootBlock.id,
          isPropagating: null,
        })
      })
    } else if (isReferenceBlock(processedData)) {
      const currentChildren = await tx.getBlockChildren({
        sourceBlockId: idToSourceBlockId(processedData),
        rootBlockId: rootBlock.id,
      })
      perfReport?.checkpoint(`updateBlock:DO_CLEANUP_CHILDREN-getBlockChildren`)
      const livingChildren = new Set<string>()
      if (processedData.variantOverrides) {
        for (const [_oKey, override] of Object.entries(
          processedData.variantOverrides
        )) {
          for (const c of override.value.targetBlockIds) {
            livingChildren.add(c)
          }
        }
      }

      for (const c of processedData.value.targetBlockIds) {
        livingChildren.add(c)
      }

      for (const c of processedData.constraints.acceptedTypes ?? []) {
        livingChildren.add(c)
      }

      await pMap(currentChildren, async (childBr) => {
        if (childBr.structural) {
          // for now, we'll spare components here
          // cause there are too many edge cases if we try to delete them here
          return
        }
        if (livingChildren.has(childBr.sourceBlockId)) {
          return
        }

        const references = await tx.getReferencesByTarget({
          targetBlockId: childBr.sourceBlockId,
          rootBlockId: rootBlock.id,
        })
        if (
          references.some(
            (r) =>
              r.id ===
              // ignore this block, cause the DB doesn't have the update that we're performing in this tx
              // if the block is still being referenced, we should not have gotten here in the first place
              processedData.id
          )
        ) {
          livingChildren.add(childBr.sourceBlockId)
          return
        }

        // else it should be cleaned up
        await blockRelationshipBaseOps.update(tx, {
          id: childBr.id,
          deletedAt: isoNow,
        })
        tx.revalidateBlockMilestonesMap.set(childBr.blockId, {
          path: normalizedPath.join('/') + '/' + childBr.blockId,
          blockId: childBr.blockId,
          isoNow,
          sourceBlockId: childBr.sourceBlockId,
          rootId: rootBlock.id,
          isPropagating: null,
        })
      })

      perfReport?.checkpoint(`updateBlock:DO_CLEANUP_CHILDREN-DONE`)
    }
  }

  const fullyUpdatedBlock: Block = {
    ...processedData,
    updatedAt: isoNow,
  }

  const isUpdatingHiddenState = processedData.hidden !== blockToUpdate.hidden

  if (isComponentBlock(fullyUpdatedBlock) && parentBlock) {
    // if parent is instance, we can't allow a component be its direct child
    // this may happen if a document child of an instance is trying to be converted into a component
    // or if a component is trying to be moved into a list
    if (isInstanceBlock(parentBlock)) {
      throw new MutatorUserError(
        `Component blocks can't be direct children of instance blocks.`
      )
    } else if (isListBlock(parentBlock)) {
      // in the case of lists, we only allow the target component, but not others.
      if (
        !skipComponentHeirarchyCheck &&
        parentBlock.typeOptions.targetBlockId !==
          fullyUpdatedBlock.sourceBlockId &&
        parentBlock.typeOptions.targetBlockId !== fullyUpdatedBlock.id
      ) {
        throw new MutatorUserError(
          `Component blocks can't be children of list blocks.`
        )
      } else {
        // comp is target block of list
        if (isUpdatingHiddenState) {
          // if we're updating hidden state, we pin it to the top of the list
          const children = await tx.getBlockChildren({
            rootBlockId: rootBlock.id,
            sourceBlockId: idToSourceBlockId(parentBlock),
          })
          const brToPin = children.find((c, i) => {
            if (c.blockId === fullyUpdatedBlock.id) {
              return i !== 0
            }
            return false
          })
          if (brToPin) {
            await blockRelationshipBaseOps.update(tx, {
              id: brToPin.id,
              order: generateKeyBetween(
                null,
                children[0]?.order ?? null,
                rootBlock.id
              ),
            })
          }
        }
      }
    }
  }

  if (isUpdatingHiddenState) {
    if (
      !ignoreHiddenPropagationWarning &&
      parentBlock &&
      'hidden' in parentBlock &&
      (shouldForcefullyPropagateHiddenState(parentBlock) || parentBlock.hidden)
    ) {
      // if parent shouldForcefullyPropagateHiddenState, then we can't edit `hidden` state directly
      throw new MutatorUserError(
        `Can't edit hidden state directly, as this block is below a ${parentBlock.type}. Edit the parent block instead.`
      )
    }
    if (isBlockWithChildren(fullyUpdatedBlock)) {
      const result = await propagateHiddenStateToChildren(tx, {
        block: fullyUpdatedBlock,
        dryRun: true,
        isoNow,
        path: normalizedPath.join('/'),
        // We always want to update rich text children
        justRichTextChildren:
          skipPropagateHiddenState &&
          !shouldForcefullyPropagateHiddenState(fullyUpdatedBlock),
        rootBlockId: rootBlock.id,
      })
      undo.push(...result.undo)
    }
  }

  /* Changing type sync:
   * Template block must descendant of a component
   * We don't need to sync slash command changes (from comment type to anything else)
   */
  if (
    blockToUpdate.type !== fullyUpdatedBlock.type &&
    isComponentBlock(parentBlock) &&
    blockToUpdate.type !== 'comment'
  ) {
    const treeManager = await tx.getTreeManager(rootBlock.id)
    const blocksToSync =
      treeManager.compoundIndexes
        .get('byInstanceChildTarget')
        ?.get(idToSourceBlockId(fullyUpdatedBlock)) ?? new Set<string>()

    await Promise.all(
      Array.from(blocksToSync).map(async (blockId) => {
        const node = treeManager.getNode(blockId, { required: true })
        invariant(
          node.value.instanceChildTarget ===
            idToSourceBlockId(fullyUpdatedBlock),
          'Instance child target does not match'
        )
        if (
          // @ts-ignore
          node.value.__virtual
        ) {
          // don't care about these
          return
        }
        const path = treeManager.getPath(node.value.id, { required: true })
        const result = await migrateCurrentBlockToNewType({
          currentBlock: node.value,
          targetBlock: fullyUpdatedBlock,
        })

        return await updateBlock(tx, {
          data: result,
          id: node.value.id,
          instanceChildTarget: node.value.instanceChildTarget,
          isoNow,
          path,
          strategy: 'merge',
        })
      })
    )
  }

  await blockBaseOps.update(tx, fullyUpdatedBlock, parentBlock.id)

  perfReport?.checkpoint(`updateBlock:DO_UPDATE_BLOCK`)

  tx.revalidateBlockMilestonesMap.set(fullyUpdatedBlock.id, {
    path,
    blockId: fullyUpdatedBlock.id,
    isoNow,
    sourceBlockId: idToSourceBlockId(fullyUpdatedBlock),
    rootId: rootBlock.id,
    isPropagating: null,
  })

  perfReport?.checkpoint(`updateBlock:REVALIDATE_BLOCK_MILESTONE`)

  return {
    updatedBlock: fullyUpdatedBlock,
    br: (await blockRelationshipBaseOps.get(tx, blockRelationship.id))!,
    updatedPath: path,
    branchId: rootBlock.branchId,
    undo,
  }
}

/* -------------------------------------------------------------------------------------------------
 * UPDATE VARIABLES AND PREVIEW URL
 * -----------------------------------------------------------------------------------------------*/

export const updatePreviewUrl = async (
  tx: WriteTransaction,
  {
    blockId: _blockId,
    path: _path,
    previewUrl,
    isoNow,
  }: WithServerSecret<{
    blockId: string
    previewUrl: string
    path: string
    isoNow: string
  }>
) => {
  ensureBaseHubTransaction(tx)

  const { block, path } = await doCopyOnWrite(tx, {
    partialBlock: { id: _blockId, instanceChildTarget: null },
    path: _path,
    isoNow,
  })

  if (previewUrl.length > 300) {
    throw new Error('Preview URL is too long')
  }
  if (!isValidBlockPreviewType(block)) {
    throw new Error(`Block with id ${block.id} is not a valid preview type`)
  }
  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })
  if (!block.previewUrl && !previewUrl) return

  await updateBlock(tx, {
    id: block.id,
    data: {
      previewUrl: previewUrl === '' ? null : previewUrl,
      type: block.type,
    },
    instanceChildTarget: block.instanceChildTarget ?? null,
    strategy: 'merge',
    path,
    isoNow,
  })
}

export type UpdateApiNameParams = {
  blockId: string
  apiName: string | null
  path: string
  isoNow: string
}

export const updateApiName = async (
  tx: WriteTransaction,
  {
    blockId: _blockId,
    path: _path,
    apiName,
    isoNow,
  }: WithServerSecret<UpdateApiNameParams>
) => {
  ensureBaseHubTransaction(tx)

  const { block, parentBlock, path, rootBlock } = await doCopyOnWrite(tx, {
    partialBlock: { id: _blockId, instanceChildTarget: null },
    path: _path,
    isoNow,
  })

  const children = await tx.getBlockChildren({
    rootBlockId: rootBlock.id,
    sourceBlockId: idToSourceBlockId(parentBlock),
  })

  const parentChildren = await getBlocks(
    tx,
    children.map((c) => c.blockId)
  )

  // -- Validations
  // API name can't be set for root or comment blocks
  if (isRootBlock(block) || isCommentBlock(block)) {
    throw new Error('API name can not be set for root or comment blocks')
  }
  // If is setting the apiName to the same value we don't need to do anything
  if (apiName === block.apiName) return
  // if API name is something, can't be larger than 100 characters
  if (apiName && apiName.length > 100) throw new Error('API name is too long')

  // Can't assign API name to instance blocks under rich text or list blocks
  if (isRichTextBlock(parentBlock) && isInstanceBlock(block)) {
    throw new Error(
      'API name can not be set for instance blocks under rich text blocks'
    )
  }
  if (isListBlock(parentBlock) && isInstanceBlock(block)) {
    throw new Error(
      'API name can not be set for instance blocks under list blocks'
    )
  }

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })
  // check if the apiName is already in use
  const apiNameExists = parentChildren.some(
    (c) => 'apiName' in c && c.apiName && c.apiName === apiName
  )
  if (apiNameExists) throw new Error('API name already in use')

  return await updateBlock(tx, {
    path,
    id: block.id,
    data: { type: block.type, apiName },
    instanceChildTarget: block.instanceChildTarget ?? null,
    strategy: 'merge',
    isoNow,
  })
}

export type UpdateSlugParams = {
  blockId: string
  slug: string | null
  path: string
  isoNow: string
}

export const updateSlug = async (
  tx: WriteTransaction,
  {
    blockId: _blockId,
    slug,
    path: _path,
    isoNow,
  }: WithServerSecret<UpdateSlugParams>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member', 'editor'],
    guestAccess: 'branch-author',
  })

  const { block, parentBlock, path, rootBlock } = await doCopyOnWrite(tx, {
    partialBlock: { id: _blockId, instanceChildTarget: null },
    path: _path,
    isoNow,
  })

  const children = await tx.getBlockChildren({
    rootBlockId: rootBlock.id,
    sourceBlockId: idToSourceBlockId(parentBlock),
  })

  const parentChildren = await getBlocks(
    tx,
    children.map((c) => c.blockId)
  )
  if (
    !(
      isInstanceBlock(block) ||
      isDocumentBlock(block) ||
      isComponentBlock(block)
    )
  ) {
    throw new Error('Slug can only be set for instance, document or component')
  }
  if (slug && slug.length > 100) throw new Error('Slug is too long')
  if (isInstanceBlock(block) && isComponentBlock(parentBlock)) {
    throw new Error(
      'Slug can not be set for instance blocks under component blocks'
    )
  }

  // check if the slug is already in use
  const slugExists = parentChildren.some(
    (c) => 'slug' in c && c.slug && c.slug === slug
  )
  if (slugExists) throw new Error('Slug already in use')

  return await updateBlock(tx, {
    path,
    id: block.id,
    data: { type: block.type, slug },
    instanceChildTarget: block.instanceChildTarget ?? null,
    strategy: 'merge',
    isoNow,
  })
}

/* -------------------------------------------------------------------------------------------------
 * DELETE
 * -----------------------------------------------------------------------------------------------*/

export type DeleteBlockParams = {
  id: string
  path: string
  isoNow: string
  idSeed: string
  blocksAlsoBeingDeleted?: string[]
  ignoreInstanceChildCheck?: boolean
  ignoreComponentDeletionCheck?: boolean
  revalidateMilestonePromises?: any[]
}

export const deleteBlock = async (
  tx: WriteTransaction,
  {
    id,
    path: _path,
    isoNow,
    idSeed,
    blocksAlsoBeingDeleted,
    ignoreInstanceChildCheck,
    ignoreComponentDeletionCheck,
    revalidateMilestonePromises: revalidateMilestonePromisesOG,
  }: WithServerSecret<DeleteBlockParams>
) => {
  ensureBaseHubTransaction(tx)

  const perfReport = undefined as undefined | PerfReport // tx.serverOnly?.perfReport

  const path = normalizeStringPath({ path: _path, edgeId: id })

  const rootBlockId = path[0]
  invariant(rootBlockId, `Root block id not found for block ${id}`)
  const parentId = path[path.length - 2]
  invariant(parentId, `Parent block id not found for block ${id}`)
  const [thisBlock, parentBlock] = await Promise.all([
    blockBaseOps.get(tx, id),
    blockBaseOps.get(tx, parentId),
  ])
  invariant(thisBlock, `Block with id ${id} not found`)
  invariant(parentBlock, `Parent block with id ${parentId} not found`)

  if (thisBlock.deletedAt) {
    return
  }

  // Consider same cases than in createBlock
  // – An editor should be able to delete the same types of blocks they can create
  // More specifically, should be able to delete any instance child of a list, rich-text or union block
  if (
    editorCanEditBlocks({
      parentBlock: parentBlock,
      blocks: [thisBlock],
    })
  ) {
    hasAccessOrThrow({
      serverOnly: tx.serverOnly,
      clientOnly: tx.clientOnly,
      serverSecret: tx.serverSecret,
      allowedRoles: ['owner', 'member', 'editor'],
      guestAccess: 'branch-author',
    })
  } else {
    hasAccessOrThrow({
      serverOnly: tx.serverOnly,
      clientOnly: tx.clientOnly,
      serverSecret: tx.serverSecret,
      allowedRoles: ['owner', 'member'],
      guestAccess: 'branch-author',
    })
  }

  perfReport?.checkpoint('deleteBlock:get-stuff')

  const parentAlsoBeingDeleted =
    blocksAlsoBeingDeleted?.includes(parentBlock.id) ||
    blocksAlsoBeingDeleted?.includes(idToSourceBlockId(parentBlock))

  if (
    parentBlock.type === 'instance' &&
    !parentAlsoBeingDeleted &&
    !ignoreInstanceChildCheck
  ) {
    throw new MutatorUserError(
      "Instance block's children can not be deleted directly."
    )
  }

  const undo: BlockBatchMutationParams['mutations'] = []

  if (isRichTextBlock(parentBlock)) {
    const blockExists = await isInstanceInRichText(
      parentBlock,
      thisBlock,
      async (sourceBlockId) =>
        (await tx.getBlockChildren({ sourceBlockId, rootBlockId })).map(
          (br) => br.blockId
        )
    )

    perfReport?.checkpoint('deleteBlock:isInstanceInRichText')

    if (blockExists && isInstanceBlock(thisBlock)) {
      // if block exists in rich text, we can delete it from nodes
      const state = parentBlock.value.state
      const json = stateToJson(state)
      const newJson = mapJSONNode(json, (node) => {
        const basehubBlockId = getIdFromBaseHubNode(node)
        if (
          node.type === BaseHubBlock.name &&
          basehubBlockId === idToSourceBlockId(thisBlock)
        ) {
          return []
        }
        if (
          node.marks?.some(
            (m) =>
              m.type === BaseHubInlineBlock.name &&
              m.attrs?.id === idToSourceBlockId(thisBlock)
          )
        ) {
          return {
            ...node,
            marks: node.marks.filter((m) => m.type !== BaseHubInlineBlock.name),
          }
        }
        return node
      })
      const newState = jsonToYDocBase64State(
        newJson,
        richTextExtensionsForTransformations
      )
      const parentPath = normalizeStringPath({
        path: path.join('/'),
        edgeId: parentBlock.id,
        includeEdge: true,
        format: 'string',
      })
      const params = {
        id: parentBlock.id,
        data: {
          type: parentBlock.type,
          value: {
            state: newState.base64,
            lastRevertedAt: isoNow,
          },
        },
        instanceChildTarget: parentBlock.instanceChildTarget ?? null,
        path: parentPath,
        strategy: 'merge-deep',
        isoNow,
      } satisfies UpdateBlockParams
      const { undo: _ } = await updateBlock(tx, params)

      perfReport?.checkpoint('deleteBlock:update-rich-text-json')

      // pushing our own undo because we need to start fresh / ditch yjs' current state
      undo.push({
        op: 'updateBlock',
        args: {
          ...params,
          data: {
            ...params.data,
            value: {
              ...params.data.value,
              state: jsonToYDocBase64State(
                stateToJson(state),
                richTextExtensionsForTransformations
              ).base64,
              lastRevertedAt: parentBlock.value.lastRevertedAt,
            } satisfies Partial<RichTextBlock['value']>,
          },
        },
      })
      return
    }
    if (isComponentBlock(thisBlock)) {
      // if block is a component, remove from accepted types and remove it
      const { allowedComponents } = parentBlock.constraints
      const newAcceptedTypes = (allowedComponents ?? []).filter(
        (id) => id !== thisBlock.id
      )
      await updateBlock(tx, {
        id: parentBlock.id,
        path: path.join('/'),
        isoNow: isoNow,
        strategy: 'merge-deep',
        instanceChildTarget: parentBlock.instanceChildTarget ?? null,
        data: {
          constraints: {
            ...parentBlock.constraints,
            allowedComponents: newAcceptedTypes,
          },
        },
      })
    }
  }

  const children = isBlockWithChildren(thisBlock)
    ? await tx.getBlockChildren({
        sourceBlockId: idToSourceBlockId(thisBlock),
        rootBlockId,
      })
    : []

  perfReport?.checkpoint('deleteBlock:get-children')

  const revalidateMilestonePromises = revalidateMilestonePromisesOG || []

  const result = await deleteBlockAndNestedBlocks(tx, {
    id: thisBlock.id,
    rootBlockId,
    path: path.join('/'),
    isoNow,
    idSeed,
    ignoreValidationForBlockIds: [
      ...(blocksAlsoBeingDeleted ?? []),
      ...(isListBlock(thisBlock) ? children.map((c) => c.blockId) : []),
    ],
    ignoreComponentDeletionCheck,
    ignoreValidationIf: (block: Block) => {
      if (!isInstanceBlock(block) || !isListBlock(thisBlock)) return false

      return (
        block.typeOptions.targetBlockId === thisBlock.typeOptions.targetBlockId
      )
    },
    preventIfTypeIsComment: isInstanceBlock(thisBlock),
    revalidateMilestonePromises,
  })

  perfReport?.checkpoint('deleteBlock:deleteBlockAndNestedBlocks')

  if (result?.undo) {
    undo.push(...result.undo)
  }

  if (!revalidateMilestonePromisesOG) {
    await Promise.all(revalidateMilestonePromises)
  }

  perfReport?.checkpoint('deleteBlock:revalidate-milestones')

  return { undo }
}

export type DeleteBlocksParams = {
  rootId: string
  blockIds: string[]
  isoNow: string
  idSeed: string
  ignoreInstanceChildCheck?: boolean
}

/**
 * Self explanatory.
 *
 * **IMPORTANT:** Assumes you're not passing a child key and then its parent.
 * You'll need to handle that filtering before passing the blockIds here, else you'll break stuff.
 * See the tree primitive for an example implementation on how selected keys are filtered by parent/child relationship.
 */
export async function deleteBlocks(
  tx: WriteTransaction,
  {
    rootId,
    blockIds,
    isoNow,
    idSeed,
    ignoreInstanceChildCheck,
  }: WithServerSecret<DeleteBlocksParams>
) {
  ensureBaseHubTransaction(tx)
  const treeManager = await tx.getTreeManager(rootId)

  const perfReport = undefined as undefined | PerfReport // tx.serverOnly?.perfReport

  perfReport?.checkpoint('deleteBlocks:getTreeManager')

  // 1. get paths for each block
  // AND merge these paths into the shortest possible quantity
  // for example, if we have ['a', 'a/b', 'a/b/c', 'a/b/c/d']
  // we can only have ['a'], as parents will clear out their children
  const { parentPaths, allBlocksBeingDeleted } =
    await getParentPathsForBulkDelete(tx, {
      blockIds,
      treeManager,
      serverSecret: tx.serverSecret,
    })

  perfReport?.checkpoint('deleteBlocks:getParentPathsForBulkDelete')

  if (DEBUG) {
    /* eslint-disable no-console */
    console.log('---------------------------------------------')
    console.log({ parentPaths })
    console.log({ allBlocksBeingDeleted })
    console.log('---------------------------------------------')
    /* eslint-enable no-console */
  }

  const undo: BlockBatchMutationParams['mutations'] = []
  const revalidateMilestonePromises: any[] = []

  // 2. call `deleteBlockOnPath` on each one.
  // unfortunately, this needs to be synchronous, as we need to delete the blocks in order
  for (const { bId, path } of parentPaths) {
    const result = await deleteBlock(tx, {
      id: bId,
      path,
      isoNow,
      idSeed,
      blocksAlsoBeingDeleted: Array.from(allBlocksBeingDeleted),
      ignoreInstanceChildCheck,
      revalidateMilestonePromises,
    })
    if (result?.undo) {
      undo.unshift(...result.undo)
    }
  }

  await Promise.all(revalidateMilestonePromises)

  perfReport?.checkpoint('deleteBlocks:deleteBlock')

  // eslint-disable-next-line no-console
  if (DEBUG) console.log({ tx })

  return { undo }
}

/**
 * Deletes block and all its nested blocks, without worrying about paths or hashes.
 * WARNING: This function should be paired with another function that updates the parent block's hash and value.
 */
export async function deleteBlockAndNestedBlocks(
  tx: WriteTransaction,
  {
    id: _id,
    rootBlockId,
    isoNow,
    idSeed,
    expectNotFoundForBlockIds,
    ignoreValidationForBlockIds,
    ignoreValidationIf,
    ignoreComponentDeletionCheck,
    preventIfTypeIsComment,
    path: _path,
    revalidateMilestonePromises,
  }: {
    id: string
    rootBlockId: string
    isoNow: string
    idSeed: string
    path: string
    parentBlock?: Block
    expectNotFoundForBlockIds?: string[]
    ignoreValidationForBlockIds?: string[]
    ignoreComponentDeletionCheck?: boolean
    ignoreValidationIf?: (block: Block) => boolean
    preventIfTypeIsComment?: boolean
    isRecursiveCall?: boolean
    revalidateMilestonePromises: any[]
  }
) {
  ensureBaseHubTransaction(tx)

  const perfReport = undefined as undefined | PerfReport // tx.serverOnly?.perfReport
  const treeManager = await tx.getTreeManager(rootBlockId)

  const {
    block,
    blockRelationship: br,
    path,
  } = await doCopyOnWrite(tx, {
    partialBlock: { id: _id },
    isoNow,
    path: _path,
    perfReport,
  }).catch((e) => {
    if (expectNotFoundForBlockIds?.includes(_id)) {
      return { block: null, blockRelationship: null, path: null }
    }
    throw e
  })

  perfReport?.checkpoint('deleteBlockAndNestedBlocks:copy-on-write')

  if (!block || !br || !path) {
    return
  }

  if (block.deletedAt) {
    return
  }

  const id = block.id
  const undo: BlockBatchMutationParams['mutations'] = []

  // Comments under instance blocks are direct references so shouldn't be deleted from here.
  if (block.type === 'comment' && preventIfTypeIsComment) {
    return
  }

  ignoreValidationForBlockIds = [
    _id,
    id,
    ...(ignoreValidationForBlockIds ?? []),
  ]

  if (isComponentBlock(block)) {
    const detachResult = await detachInstancesOfComponent(tx, {
      sourceBlockId: idToSourceBlockId(block),
      rootBlockId,
      idSeed,
      isoNow,
      nonPromotableBlockIds: [...ignoreValidationForBlockIds].filter(Boolean),
    })
    if (detachResult?.undo) {
      undo.unshift(...detachResult.undo)
    }
    const detachRefTargetsResult = await detachBlocksFromTarget(tx, {
      isoNow,
      rootBlockId,
      sourceBlockId: idToSourceBlockId(block),
    })
    if (detachRefTargetsResult.undo) {
      undo.unshift(...detachRefTargetsResult.undo)
    }
    const detatchFromRichTextResult = await syncAllRichTextsUsingComponent(tx, {
      componentBlockId: idToSourceBlockId(block),
      isoNow,
      rootBlockId,
      expectNotFoundForBlockIds: [_id, block.id, idToSourceBlockId(block)],
    })
    if (detatchFromRichTextResult.undo) {
      undo.unshift(...detatchFromRichTextResult.undo)
    }
    if (!ignoreComponentDeletionCheck) {
      await checkComponentDeletion(tx, {
        rootBlockId,
        component: block,
        ignoreValidationForBlockIds,
      })
    }
  }

  if (isInstanceBlock(block)) {
    const detachRefTargetsResult = await detachBlocksFromTarget(tx, {
      isoNow,
      sourceBlockId: idToSourceBlockId(block),
      rootBlockId,
      perfReport,
    })
    if (detachRefTargetsResult.undo) {
      undo.unshift(...detachRefTargetsResult.undo)
    }
  }

  perfReport?.checkpoint('deleteBlockAndNestedBlocks:CHECKS')

  // 1. delete from bottom to top
  if (isBlockWithChildren(block)) {
    const childrenKeys = treeManager.getChildrenKeys(block.id)

    ignoreValidationForBlockIds.push(...childrenKeys)

    for (const c of childrenKeys) {
      const result = await deleteBlockAndNestedBlocks(tx, {
        revalidateMilestonePromises,
        id: c,
        idSeed,
        rootBlockId,
        isoNow,
        preventIfTypeIsComment: isInstanceBlock(block),
        expectNotFoundForBlockIds,
        isRecursiveCall: true,
        ignoreValidationForBlockIds,
        ignoreValidationIf,
        parentBlock: block,
        path: normalizeStringPath({
          path,
          edgeId: c,
          format: 'string',
          includeEdge: false,
        }),
      })
      if (result?.undo) {
        undo.unshift(...result.undo)
      }
    }
  }

  perfReport?.checkpoint('deleteBlockAndNestedBlocks:CHILDREN')

  invariant(!br.isCommitted, `Block relationship ${br.id} is committed`)
  await blockRelationshipBaseOps.update(tx, { id: br.id, deletedAt: isoNow })
  await blockBaseOps.update(tx, { id, deletedAt: isoNow })
  revalidateMilestonePromises.push(
    revalidateBlockMilestone(tx, {
      path,
      sourceBlockId: idToSourceBlockId(block),
      rootId: rootBlockId,
      isoNow,
      blockId: id,
      isPropagating: null,
    })
  )

  undo.unshift({
    op: 'reviveBlock',
    args: { brId: br.id, rootBlockId, isoNow, path },
  })

  return { undo }
}

/* -------------------------------------------------------------------------------------------------
 * DUPLICATE
 * -----------------------------------------------------------------------------------------------*/

export type DuplicateBlockParams = {
  path: string
  duplicatedBlockId: string
  newBlockId: string
  isoNow: string
  idSeed: string
  index?: number
  titleUpdateStrategy?: 'incr' | 'equal' | 'untitled'
  duplicatedComponentStrategy?:
    | 'create-instance'
    | 'default'
    | 'create-component'
  skipBrCreation?: boolean
  isNestedCall?: boolean
  forceTargetBlockIdIfInstance?: string
  position?: Position
  instanceChildTarget?: Block['instanceChildTarget']
  isVirtualized?: boolean
}

export const duplicateBlock = async (
  tx: WriteTransaction,
  {
    path,
    duplicatedBlockId,
    newBlockId,
    isoNow,
    idSeed,
    position,
    titleUpdateStrategy = 'incr',
    duplicatedComponentStrategy = 'default',
    skipBrCreation,
    isNestedCall,
    forceTargetBlockIdIfInstance,
    instanceChildTarget,
    isVirtualized,
  }: WithServerSecret<DuplicateBlockParams>
) => {
  ensureBaseHubTransaction(tx)

  path = normalizeStringPath({
    path,
    edgeId: duplicatedBlockId,
    includeEdge: false, // remove duplicate from path
    format: 'string',
  })
  const pathArray = path.split('/')

  const rootBlockId = pathArray[0]
  invariant(rootBlockId, 'rootBlockId is required')

  const treeManager = await tx.getTreeManager(rootBlockId)
  const brs = await tx.getBrsByRootId(treeManager.root.value as RootBlock)

  const node = treeManager.getNode(duplicatedBlockId, { required: true })
  const block = node.value
  const undo: BlockBatchMutationParams['mutations'] = []
  const parentBlockId = node.parentKey
  invariant(parentBlockId, 'Parent block id not found')

  const parentInTargetPathPreCoW = pathArray[pathArray.length - 1]
  invariant(parentInTargetPathPreCoW, 'parentInTargetPathPreCoW is required')

  const parentBlock = treeManager.getNode(parentBlockId, {
    required: true,
  }).value

  const isNotSibling =
    parentInTargetPathPreCoW !== parentBlock.id ||
    parentInTargetPathPreCoW !== idToSourceBlockId(parentBlock)

  if (isInstanceBlock(parentBlock) && isCommentBlock(block)) {
    // skip duplicating comments under instance blocks
    return null
  }

  if (isInstanceBlock(parentBlock) && !isNestedCall) {
    throw new MutatorUserError(
      "Instance block's children can not be duplicated directly."
    )
  }
  invariant(
    isBlockWithChildren(parentBlock),
    `wrong parent block was retrieved for block with id ${block.id}, retrieved: ${parentBlock.id}, reason: block is not a block with children.`
  )

  /**
   * Duplicate block strategy:
   *   - If duplicate a collection
   *      - If has component in the children, duplicate component and change the targetBlockId to the new component
   *       - else duplicate as component
   *
   */

  const isParentsTargetBlockIdInList =
    isListBlock(parentBlock) &&
    block.id === parentBlock.typeOptions.targetBlockId

  const isDuplicatingComponent = isComponentBlock(block)

  let incrementalTitle: string | undefined
  if (titleUpdateStrategy === 'incr') {
    const siblings = await Promise.all(
      (treeManager.getChildrenKeys(parentBlock.id) ?? []).map(
        async (id) => await blockBaseOps.get(tx, id)
      )
    )
    incrementalTitle = getTitleForDuplicate(
      block.title,
      siblings.filter(Boolean)
    )
  }

  let shouldDuplicateAsInstance = false
  if (
    isDuplicatingComponent &&
    duplicatedComponentStrategy !== 'create-component'
  ) {
    // if duplicating component, we may create an instance of it
    if (isNestedCall) {
      // if (!isParentsTargetBlockIdInList) {
      //   // if is nested call but inside list, then we duplicate as component
      //   shouldDuplicateAsInstance = true
      // }
    } else {
      if (isParentsTargetBlockIdInList) {
        // if not nested call but inside list, then we duplicate as instance
        shouldDuplicateAsInstance = true
      }
    }

    if (duplicatedComponentStrategy === 'create-instance') {
      shouldDuplicateAsInstance = true
    }

    if (
      isParentsTargetBlockIdInList &&
      isNestedCall &&
      duplicatedComponentStrategy === 'create-instance'
    ) {
      shouldDuplicateAsInstance = true
      if (block.hidden) return
    }
  }
  // If duplicating a list and is a nested call, we should typeoptions to point to the targetBlockId of the parent list block

  // 1. create new fresh block
  const newData = {
    ...block,
    ...(block.type === 'rich-text'
      ? {
          value: {
            ...block.value,
            lastRevertedAt: isoNow,
          } satisfies RichTextBlock['value'],
        }
      : {}),
    ...(isDuplicatingComponent && shouldDuplicateAsInstance
      ? ({
          type: 'instance',
          hidden: false,
          typeOptions: {
            targetBlockId: idToSourceBlockId(block),
          },
          value: {},
        } satisfies AtLeast<InstanceBlock, 'type'>)
      : {}),
    instanceChildTarget,
    apiName: null,
    slug: null,
  } as AtLeast<Block, 'type'>

  if (newData.type === 'instance' && forceTargetBlockIdIfInstance) {
    // get targetBlockId make sure its real
    const targetBlock = treeManager.getNode(forceTargetBlockIdIfInstance)?.value
    invariant(
      targetBlock,
      `Target block with id ${forceTargetBlockIdIfInstance} not found`
    )
    invariant(
      isComponentBlock(targetBlock),
      `Target block with id ${forceTargetBlockIdIfInstance} is not a component block`
    )
    newData.typeOptions = { targetBlockId: idToSourceBlockId(targetBlock) }
  }

  const {
    newBlock: _freshNewBlock,
    newBlockPath: pathToNewBlock,
    newBlockRelationship: freshNewBlockRelationship,
    undo: undoForNewBlock,
  } = isVirtualized
    ? run(() => {
        const newBlock = treeManager.getNode(newBlockId, {
          required: true,
        }).value
        const newBlockPath = treeManager.getPath(newBlockId, { required: true })
        const newBlockRelationship = brs.get(newBlockId)
        return { newBlock, newBlockPath, newBlockRelationship, undo: [] }
      })
    : await createBlock(tx, {
        data: {
          ...newData,
          id: newBlockId,
          sourceBlockId: null,
          title: match(titleUpdateStrategy)
            .with('incr', () => incrementalTitle)
            .with('equal', () => block.title)
            .with('untitled', () => '')
            .exhaustive(),
        },
        parentInstanceChildTarget: parentBlock.instanceChildTarget,
        path,
        isoNow,
        idSeed: (idSeed = getIdempotentId({ idSeed })),
        skipRehashing: true, // will update this later on
        skipBrCreation,
        position:
          (position ?? isNotSibling)
            ? { type: 'last' }
            : { type: 'after', targetId: block.id },
      })
  let freshNewBlock = _freshNewBlock
  undo.push(...undoForNewBlock)

  // correct the list's targetBlockId if it's descendant of an instance
  if (
    parentBlock &&
    isInstanceBlock(parentBlock) &&
    isListBlock(freshNewBlock)
  ) {
    freshNewBlock = structuredClone(freshNewBlock)
    const corrected = makeInstanceChildBlock({
      block: freshNewBlock,
      parentSourceBlockId: idToSourceBlockId(parentBlock),
      targetBlock: treeManager.getNode(parentBlock.typeOptions.targetBlockId, {
        required: true,
      }).value as ListBlock,
    })
    freshNewBlock.typeOptions.targetBlockId =
      corrected.typeOptions.targetBlockId
  }

  const parentInTargetPathKey = normalizeStringPath({
    path: pathToNewBlock,
    edgeId: freshNewBlock.id,
    includeEdge: false,
  }).at(-1)

  invariant(parentInTargetPathKey, 'parentInTargetPathKey is required')

  const parentInTargetPathNode = treeManager.getNode(parentInTargetPathKey, {
    required: true,
  })

  treeManager.upsertNode(freshNewBlock, {
    parentKey: parentInTargetPathNode.key,
    order: freshNewBlockRelationship?.order,
  })

  let newBlockUpdates: Pick<Block, 'value'> | null = null
  if (isBlockWithChildren(block)) {
    // for blocks with children, we'll need to recursively duplicate children
    const childrenSourceBlockIdMap = new Map<string, BlockRelationship>()

    // bring target component to the top of the list so it's duplicated first
    const children = treeManager.getChildrenKeys(idToSourceBlockId(block))
    const sortedChildren = isListBlock(freshNewBlock)
      ? [...children].sort((a, b) => {
          const aNode = treeManager.getNode(a, { required: true })
          const bNode = treeManager.getNode(b, { required: true })
          if (
            freshNewBlock.typeOptions.targetBlockId ===
            idToSourceBlockId(aNode.value)
          ) {
            return -1
          }
          if (
            freshNewBlock.typeOptions.targetBlockId ===
            idToSourceBlockId(bNode.value)
          ) {
            return 1
          }
          return 0
        })
      : [...children]

    for (const childId of sortedChildren) {
      const childNode = treeManager.getNode(childId, {
        required: true,
      })
      const newBlockId = !isVirtualInstanceChildTarget(childNode.value)
        ? (idSeed = getIdempotentId({ idSeed }))
        : getInstanceChildId({
            parentSourceBlockId: idToSourceBlockId(freshNewBlock),
            targetSourceBlockId:
              childNode.value.instanceChildTarget ||
              childNode.value.sourceBlockId ||
              childNode.value.id,
          })

      // re-fetch the new block as it might have been updated in a previous iteration
      const freshNewBlockUpToDate = await blockBaseOps.get(tx, freshNewBlock.id)
      invariant(
        freshNewBlockUpToDate,
        `Block with id ${freshNewBlock.id} not found`
      )

      if (
        parentBlock &&
        isInstanceBlock(parentBlock) &&
        isListBlock(freshNewBlockUpToDate)
      ) {
        // skip duplicating the targetBlockId as it's duplicating an instance...
        if (childNode.value.type === 'component') continue
      }
      let instanceChildTarget = null
      if (shouldDuplicateAsInstance) {
        instanceChildTarget = idToSourceBlockId(childNode.value)
      } else {
        instanceChildTarget = childNode.value.instanceChildTarget
      }

      if (
        parentBlock &&
        // for now, skip it regardless of the parent block type
        // isInstanceBlock(parentBlock) &&
        isReferenceBlock(freshNewBlockUpToDate)
      ) {
        // skip duplicating components as it's duplicating an instance...
        if (childNode.value.type === 'component') continue
      }

      /**
       * We are in a list block, and we're about to duplicate its rows.
       * we must make sure that the each row's targetBlockId is the new list block's targetBlockId.
       */
      const forceTargetBlockIdIfInstance = isListBlock(freshNewBlockUpToDate)
        ? freshNewBlockUpToDate.typeOptions.targetBlockId
        : undefined

      const result = await duplicateBlock(tx, {
        // We know that if is a nested call, we already passed the auth for the parentBlock, so we just bypass any other auth checks.
        serverSecret: tx.serverOnly
          ? process.env.MUTATOR_SERVER_AUTH_SECRET
          : undefined,
        path: pathToNewBlock,
        duplicatedBlockId: childId,
        newBlockId,
        instanceChildTarget,
        isoNow,
        idSeed: getIdempotentId({
          idSeed,
          splitPaths: { newPath: childId },
        }),
        titleUpdateStrategy: 'equal',
        isNestedCall: true,
        duplicatedComponentStrategy: shouldDuplicateAsInstance
          ? // if duplicating an instance force nested stuff to be instance as well.
            'create-instance'
          : // else handle it as normal
            'default',
        forceTargetBlockIdIfInstance,
        isVirtualized: !!isVirtualInstanceChildTarget(childNode.value),
      })

      const duplicated = result?.newBlock
      if (result?.undo) {
        undo.push(...result.undo)
      }

      // We are in a list block, and we have just duplicated the targetBlockId. So we should set this to the new list block targetBlockId
      if (
        isListBlock(block) &&
        isListBlock(freshNewBlockUpToDate) &&
        duplicated &&
        duplicated.type === 'component' &&
        idToSourceBlockId(childNode.value) === block.typeOptions.targetBlockId
      ) {
        freshNewBlockUpToDate.typeOptions.targetBlockId =
          idToSourceBlockId(duplicated)
        // quickly send this partial update for subsequent children to use the new targetBlockId
        await updateBlock(tx, {
          id: freshNewBlockUpToDate.id,
          data: {
            type: freshNewBlockUpToDate.type,
            typeOptions: {
              targetBlockId: freshNewBlockUpToDate.typeOptions.targetBlockId,
            },
          },
          instanceChildTarget: freshNewBlockUpToDate.instanceChildTarget,
          isoNow,
          path: pathToNewBlock,
          strategy: 'merge-deep',
          skipPropagateHiddenState: true,
          ignoreHashComparison: true,
        })
        // not including this undo as the freshNewBlockUpToDate will be deleted on undo
      }

      // We are in a reference block, and we have just duplicated one of its children
      // we re-target the ref to point to the new duplicated child
      if (isReferenceBlock(freshNewBlockUpToDate) && duplicated) {
        if (duplicated.type === 'instance') {
          freshNewBlockUpToDate.value.targetBlockIds =
            freshNewBlockUpToDate.value.targetBlockIds.map((id) => {
              if (id === idToSourceBlockId(childNode.value)) {
                return idToSourceBlockId(duplicated)
              }
              return id
            })
        }
        // disabling this behavior for now as we'd still need to re-target other child instances
        // all of this is a mess... we need to refactor this function at some point.
        // else if (duplicated.type === 'component') {
        //   freshNewBlockUpToDate.constraints.acceptedTypes =
        //     freshNewBlockUpToDate.constraints.acceptedTypes?.map((id) => {
        //       if (id === idToSourceBlockId(childNode.value)) {
        //         return idToSourceBlockId(duplicated)
        //       }
        //       return id
        //     }) ?? null
        // }

        await updateBlock(tx, {
          id: freshNewBlockUpToDate.id,
          data: freshNewBlockUpToDate,
          instanceChildTarget: freshNewBlockUpToDate.instanceChildTarget,
          isoNow,
          path: pathToNewBlock,
          strategy: 'replace',
          skipPropagateHiddenState: true,
          ignoreHashComparison: true,
        })
        // not including this undo as the freshNewBlockUpToDate will be deleted on undo
      }

      if (result?.newBlockRelationship) {
        childrenSourceBlockIdMap.set(
          idToSourceBlockId(childNode.value),
          result.newBlockRelationship
        )
      }
    }

    const freshNewBlockUpToDate = await blockBaseOps.get(tx, freshNewBlock.id)
    invariant(
      freshNewBlockUpToDate,
      `Block with id ${freshNewBlock.id} not found`
    )

    if (
      isRichTextBlock(freshNewBlockUpToDate) &&
      childrenSourceBlockIdMap.size
    ) {
      const { state: updatedState } = updateRichTextStateBasedOnNewChildren({
        state: freshNewBlockUpToDate.value.state,
        newChildrenMap: childrenSourceBlockIdMap,
      })

      newBlockUpdates = {
        value: {
          ...block.value,
          state: updatedState.base64,
        },
      }
    }
  }

  let freshNewBlockUpToDate = await blockBaseOps.get(tx, freshNewBlock.id)
  invariant(
    freshNewBlockUpToDate,
    `Block with id ${freshNewBlock.id} not found`
  )

  // 2. update new block with new value and typeOptions
  if (newBlockUpdates) {
    const { updatedBlock } = await updateBlock(tx, {
      id: freshNewBlockUpToDate.id,
      data: {
        type: freshNewBlockUpToDate.type,
        ...(newBlockUpdates as Record<string, unknown>),
      },
      instanceChildTarget: freshNewBlockUpToDate.instanceChildTarget,
      isoNow,
      path: pathToNewBlock,
      strategy: 'merge-deep',
      skipPropagateHiddenState: true,
      ignoreHashComparison: true,
      skipComponentHeirarchyCheck: true,
    })
    freshNewBlockUpToDate = updatedBlock
    // not including this undo as the freshNewBlock will be deleted on undo
  }

  return {
    newBlock: freshNewBlock,
    newBlockPath: pathToNewBlock,
    newBlockRelationship: freshNewBlockRelationship,
    undo,
  }
}

/* -------------------------------------------------------------------------------------------------
 * REVIVE (useful for undo/redo) kind of the inverse of deleteBlockAndNestedBlocks
 * -----------------------------------------------------------------------------------------------*/

export type ReviveBlockParams = {
  brId: string
  rootBlockId: string
  isoNow: string
  path: string
}

export const reviveBlock = async (
  tx: WriteTransaction,
  { brId, path, isoNow, rootBlockId }: WithServerSecret<ReviveBlockParams>
) => {
  ensureBaseHubTransaction(tx)

  const br = await tx.get<BlockRelationship>(`br/${brId}`)
  invariant(br, `Block relationship with id ${brId} not found`)
  const updatedBr = { ...br, deletedAt: null }
  await blockRelationshipBaseOps.update(tx, {
    id: brId,
    deletedAt: updatedBr.deletedAt,
  })

  const block = await tx.get<Block>(`block/${br.blockId}`)
  invariant(block, `Block with id ${br.blockId} not found`)
  const updatedBlock = {
    ...block,
    deletedAt: null,
  }
  if (block.deletedAt) {
    await blockBaseOps.update(tx, {
      id: block.id,
      deletedAt: updatedBlock.deletedAt,
    })

    tx.revalidateBlockMilestonesMap.set(block.id, {
      path,
      sourceBlockId: idToSourceBlockId(updatedBlock as Block),
      blockId: block.id,
      rootId: rootBlockId,
      isoNow,
      isPropagating: null,
    })
  }

  return { block: updatedBlock, br: updatedBr }
}

/* -------------------------------------------------------------------------------------------------
 * READ (helpers)
 * -----------------------------------------------------------------------------------------------*/

function getTitleForDuplicate(title: string, siblings: Block[]) {
  const matches = title.match(/^(.*?)(?:\s*\((\d+)\))?$/)
  const baseTitle = matches?.[1]
  const existingCount = parseInt(matches?.[2] ?? '1')

  let newTitle = baseTitle
  let counter = existingCount

  while (
    siblings.some((obj) => {
      const matches = title.match(/^(.*?)(?:\s*\((\d+)\))?$/)

      return (
        obj.title === newTitle ||
        (obj.title === `${baseTitle || 'Untitled'} (${counter})` &&
          parseInt(matches?.[2] ?? '1') >= counter)
      )
    })
  ) {
    newTitle = !counter
      ? `${baseTitle || 'Untitled'}`
      : `${baseTitle || 'Untitled'} (${counter})`
    counter++
  }

  return newTitle
}

const getParentPathsForBulkDelete = async (
  tx: BaseHubTransaction,
  {
    blockIds,
    treeManager,
  }: WithServerSecret<{
    blockIds: string[]
    treeManager: TreeManager<Block, CommonBlocksCompoundIndexes>
  }>
) => {
  const allBlocksBeingDeleted = new Set<string>()

  const blockPaths = (
    await Promise.all(
      blockIds.map(async (bId) => {
        const node =
          treeManager.getNode(bId) ??
          treeManager.getNode(
            getCoRBlockKey({
              rootBlockId: treeManager.root.value.id,
              sourceBlockId: bId,
            })
          )
        if (!node) {
          throw new Error(
            `getParentPathsForBulkDelete: Block with id ${bId} not found`
          )
        }

        const parentBlock = node.parentKey
          ? treeManager.getNode(node.parentKey)
          : null

        if (
          editorCanEditBlocks({
            parentBlock: parentBlock?.value,
            blocks: [node.value],
          })
        ) {
          hasAccessOrThrow({
            clientOnly: tx.clientOnly,
            serverOnly: tx.serverOnly,
            serverSecret: tx.serverSecret,
            allowedRoles: ['owner', 'member', 'editor'],
            guestAccess: 'branch-author',
          })
        } else {
          hasAccessOrThrow({
            clientOnly: tx.clientOnly,
            serverOnly: tx.serverOnly,
            serverSecret: tx.serverSecret,
            allowedRoles: ['owner', 'member'],
            guestAccess: 'branch-author',
          })
        }

        const path = treeManager.getPath(node, {
          format: 'string',
          required: true,
        })

        allBlocksBeingDeleted.add(node.value.id)
        allBlocksBeingDeleted.add(idToSourceBlockId(node.value))

        return {
          path,
          bId: node.value.id,
          sourceBlockId: idToSourceBlockId(node.value),
        }
      })
    )
  )
    .sort((a, b) => a.path.localeCompare(b.path))
    .reduce<{ path: string; bId: string; sourceBlockId: string }[]>(
      (acc, currentPath) => {
        if (acc.length === 0) {
          acc.push(currentPath)
          return acc
        }

        const isSubPath = (path1: string, path2: string) =>
          path1.startsWith(path2) || path2.startsWith(path1)
        const existingPathIndex = acc.findIndex((existingPath) =>
          isSubPath(existingPath.path, currentPath.path)
        )

        if (existingPathIndex !== -1) {
          const existingPath = acc[existingPathIndex]
          if (currentPath.path.length < existingPath!.path.length) {
            acc[existingPathIndex] = currentPath
          }
        } else {
          acc.push(currentPath)
        }

        return acc
      },
      []
    )

  return {
    parentPaths: blockPaths,
    allBlocksBeingDeleted: Array.from(allBlocksBeingDeleted),
  }
}
